import { Controller } from "@hotwired/stimulus"

// Multi-marker Google Map with suburb search.
// Shows all partner/resort markers on a map of Australia.
// Search geocodes a suburb and zooms to that area.
export default class extends Controller {
  static targets = ["searchInput", "mapCanvas"]
  static values = {
    locations: Array,
    apiKey: String,
    primaryColor: String,
    secondaryColor: String,
    surfaceColor: String,
    neutralColor: String,
    textColor: String,
    textMutedColor: String
  }

  // Fallback view only — used when there is nothing to fit to. The map normally
  // frames whatever markers it is given (see fitToMarkers), so adding partners
  // in another state moves the view without a code change.
  static DEFAULT_CENTER = { lat: -25.6, lng: 134.4 } // centre of Australia
  static DEFAULT_ZOOM = 4
  // A lone marker has no meaningful bounds; fitBounds would zoom to street level.
  static SINGLE_MARKER_ZOOM = 11

  connect() {
    if (!this.locationsValue || this.locationsValue.length === 0) {
      this.showError("No locations provided")
      return
    }

    if (!this.apiKeyValue) {
      this.showError("Google Maps API key is missing")
      return
    }

    if (window.google && window.google.maps) {
      this.initializeMap()
    } else {
      this.loadGoogleMapsAPI()
    }
  }

  loadGoogleMapsAPI() {
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]')
    if (existingScript) {
      const poll = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(poll)
          this.initializeMap()
        }
      }, 100)
      return
    }

    const script = document.createElement("script")
    const callbackName = `initGoogleMaps_${Date.now()}`
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKeyValue}&libraries=places&callback=${callbackName}`
    script.async = true
    script.defer = true

    script.onerror = () => this.showError("Failed to load Google Maps")

    window[callbackName] = () => {
      delete window[callbackName]
      this.initializeMap()
    }

    document.head.appendChild(script)
  }

  initializeMap() {
    try {
      const locations = this.locationsValue

      const mapEl = this.hasMapCanvasTarget ? this.mapCanvasTarget : this.element
      mapEl.innerHTML = ""

      this.map = new google.maps.Map(mapEl, {
        zoom: this.constructor.DEFAULT_ZOOM,
        center: this.constructor.DEFAULT_CENTER,
        styles: this.mapStyles(),
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        gestureHandling: "cooperative"
      })

      this.infoWindow = new google.maps.InfoWindow()
      this.markers = []
      this.pulseMarkers = []

      locations.forEach((loc) => {
        const position = { lat: loc.latitude, lng: loc.longitude }

        // Outer pulse ring (animated glow)
        const pulseMarker = new google.maps.Marker({
          position,
          map: this.map,
          clickable: false,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 16,
            fillColor: this.primaryColorValue,
            fillOpacity: 0.25,
            strokeColor: this.primaryColorValue,
            strokeWeight: 1,
            strokeOpacity: 0.4
          },
          zIndex: 1
        })
        this.pulseMarkers.push(pulseMarker)

        const marker = new google.maps.Marker({
          position,
          map: this.map,
          title: loc.name,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: this.primaryColorValue,
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 3,
            strokeOpacity: 1
          },
          animation: google.maps.Animation.DROP,
          zIndex: 2
        })

        const link = loc.slug.startsWith("/") ? loc.slug : `/locations/${loc.slug}`
        const linkText = loc.slug.startsWith("/") ? "View details →" : "View resort →"
        const content =
          `<div style="padding: 10px; max-width: 220px;">` +
          `<h3 style="color: ${this.primaryColorValue}; margin: 0 0 6px; font-size: 14px; font-weight: 600;">${loc.name}</h3>` +
          `<p style="margin: 0 0 6px; color: ${this.textMutedColorValue}; font-size: 13px; line-height: 1.4;">${loc.address}</p>` +
          `<a href="${link}" style="color: ${this.primaryColorValue}; text-decoration: none; font-size: 12px; font-weight: 500;">${linkText}</a>` +
          `</div>`

        marker.addListener("click", () => {
          this.infoWindow.setContent(content)
          this.infoWindow.open(this.map, marker)
        })

        this.markers.push(marker)
      })

      this.fitToMarkers(locations)
      this.startPulseAnimation()
      this.initializeSearch()
    } catch (error) {
      this.showError(`Map initialization failed: ${error.message}`)
    }
  }

  startPulseAnimation() {
    let growing = true
    let scale = 16
    const MIN = 14
    const MAX = 22

    this.pulseInterval = setInterval(() => {
      scale += growing ? 0.5 : -0.5
      if (scale >= MAX) growing = false
      if (scale <= MIN) growing = true

      const icon = {
        path: google.maps.SymbolPath.CIRCLE,
        scale,
        fillColor: this.primaryColorValue,
        fillOpacity: 0.15 + 0.15 * ((MAX - scale) / (MAX - MIN)),
        strokeColor: this.primaryColorValue,
        strokeWeight: 1,
        strokeOpacity: 0.2 + 0.3 * ((MAX - scale) / (MAX - MIN))
      }

      this.pulseMarkers.forEach((m) => m.setIcon(icon))
    }, 50)
  }

  initializeSearch() {
    if (!this.hasSearchInputTarget) return

    // Wait for Places library
    const waitForPlaces = () => {
      if (google.maps.places && google.maps.places.Autocomplete) {
        this.autocomplete = new google.maps.places.Autocomplete(this.searchInputTarget, {
          types: ["(regions)"],
          componentRestrictions: { country: "au" },
          fields: ["geometry", "formatted_address"]
        })

        this.autocomplete.addListener("place_changed", () => this.handlePlaceChanged())
      } else {
        setTimeout(waitForPlaces, 100)
      }
    }
    waitForPlaces()
  }

  handlePlaceChanged() {
    const place = this.autocomplete.getPlace()
    if (!place.geometry) return

    const loc = place.geometry.location

    if (place.geometry.viewport) {
      this.map.fitBounds(place.geometry.viewport)
    } else {
      this.map.setCenter(loc)
      this.map.setZoom(13)
    }
  }

  // Frame every marker. Replaces a hardcoded Sydney-region centre that left
  // partners in other states off-screen on load.
  fitToMarkers(locations) {
    const points = (locations || []).filter(
      (loc) => Number.isFinite(loc.latitude) && Number.isFinite(loc.longitude)
    )

    if (points.length === 0) {
      this.map.setCenter(this.constructor.DEFAULT_CENTER)
      this.map.setZoom(this.constructor.DEFAULT_ZOOM)
      return
    }

    if (points.length === 1) {
      this.map.setCenter({ lat: points[0].latitude, lng: points[0].longitude })
      this.map.setZoom(this.constructor.SINGLE_MARKER_ZOOM)
      return
    }

    const bounds = new google.maps.LatLngBounds()
    points.forEach((loc) => bounds.extend({ lat: loc.latitude, lng: loc.longitude }))
    this.map.fitBounds(bounds, 48)
  }

  // Reset map to the marker view the page loaded with
  reset() {
    this.fitToMarkers(this.locationsValue)
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    this.infoWindow.close()
  }

  mapStyles() {
    return [
      { featureType: "all", elementType: "geometry.fill", stylers: [{ color: this.surfaceColorValue }] },
      { featureType: "road", elementType: "geometry", stylers: [{ color: this.neutralColorValue }] },
      { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: this.primaryColorValue }, { weight: 0.5 }] },
      { featureType: "road.highway", elementType: "geometry", stylers: [{ color: this.secondaryColorValue }] },
      { featureType: "landscape", elementType: "geometry", stylers: [{ color: this.surfaceColorValue }] },
      { featureType: "poi.park", elementType: "geometry", stylers: [{ color: this.neutralColorValue }] },
      { featureType: "water", elementType: "geometry", stylers: [{ color: this.primaryColorValue }, { lightness: 70 }] },
      { featureType: "all", elementType: "labels.text.fill", stylers: [{ color: this.textColorValue }] },
      { featureType: "all", elementType: "labels.text.stroke", stylers: [{ color: this.surfaceColorValue }, { weight: 2 }] },
      { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: this.primaryColorValue }, { weight: 0.5 }] }
    ]
  }

  showError(message) {
    const el = this.hasMapCanvasTarget ? this.mapCanvasTarget : this.element
    el.innerHTML =
      `<div class="flex items-center justify-center h-full">` +
      `<div class="text-center p-4">` +
      `<i class="fas fa-exclamation-triangle text-2xl text-red-500 mb-3"></i>` +
      `<p class="text-sm font-medium text-red-500">${message}</p>` +
      `</div></div>`
  }

  disconnect() {
    if (this.pulseInterval) clearInterval(this.pulseInterval)
    if (this.infoWindow) this.infoWindow.close()
    this.map = null
    this.infoWindow = null
    this.markers = null
    this.pulseMarkers = null
  }
}
